<template>
	<section
		class="app-sidebar sidebar-border-animation"
		:class="{
			'app-sidebar--open': modelValue === true,
			'app-sidebar--closed': modelValue === false && locked === 'pinned',
			'app-sidebar--pinned-shut': locked === 'pinned-shut',
		}"
		@mouseenter="handleSidebarHover(true)"
		@mouseleave="handleSidebarHover(false)"
	>
		<div class="app-sidebar--content">
			<ul class="sidebar-items">
				<div class="first-group">
					<li class="sidebar-item">
						<div class="sidebar-item-header">
							<div class="toggleable-header">
								<slot name="header">
									<img src="../../assets/images/ls-logo.png" />
								</slot>
							</div>
						</div>
					</li>
					<li class="sidebar-item">
						<ul class="navigation-menu">
							<li class="sub-menu-item" :class="{ active: isActiveRoute('/') }" v-if="noHome !== true">
								<NuxtLink to="/">
									<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
										<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
											<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
											<path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
										</g>
									</svg>
									<h3>Home</h3>
								</NuxtLink>
							</li>

							<slot> </slot>
						</ul>
					</li>
				</div>
				<div class="last-group">
					<li>
						<div class="lock-sidebar-group" :class="{ hide: !modelValue && locked !== 'pinned-shut', locked: locked === 'pinned' }">
							<div class="lock-button-wrapper">
								<UiButton varient="outlined" class="lock-toggle" @click="cyclePinState">
									{{ locked === "pinned-shut" ? "" : pinStateDisplay.label }}
								</UiButton>
								<UiIconLock v-if="locked === 'pinned-shut'" class="icon" />
							</div>
						</div>

						<slot name="footer"></slot>
					</li>
				</div>
			</ul>
		</div>
	</section>
</template>

<script lang="ts" setup>
type PinState = "unpinned" | "pinned" | "pinned-shut";

type LSNavigationSidebarProps = {
	modelValue?: boolean;
	locked: PinState;
	noHome?: boolean;
	logoutOnly?: boolean;
	preferHeight?: boolean;
};

const props = defineProps<LSNavigationSidebarProps>();

const emit = defineEmits<{
	(e: "update:modelValue", value: boolean): void;
	(e: "update:locked", value: PinState): void;
	(e: "selecting-account", value: boolean): void;
}>();

const logoHeightStyle = computed(() => (props.preferHeight ? "100%" : "auto"));

const pinStates: PinState[] = ["unpinned", "pinned", "pinned-shut"];

function cyclePinState() {
	const currentIndex = pinStates.indexOf(props.locked);
	const nextIndex = (currentIndex + 1) % pinStates.length;
	emit("update:locked", pinStates[nextIndex] as PinState);
}

const pinStateDisplay = computed(() => {
	switch (props.locked) {
		case "pinned":
			return { label: "Pinned", icon: "pi pi-lock" };
		case "pinned-shut":
			return { label: "Pinned Shut", icon: "pi pi-lock" };
		default:
			return { label: "Unpinned", icon: "pi pi-lock-open" };
	}
});

function setOpen(value: boolean) {
	emit("update:modelValue", value);
}

const isSelectingAccount = ref(false);

function handleSidebarHover(isHovered: boolean) {
	if (isSelectingAccount.value) {
		return;
	}

	// Don't respond to hover when pinned or pinned-shut
	if (props.locked === "pinned" || props.locked === "pinned-shut") {
		return;
	}

	setOpen(isHovered);
}

const route = useRoute();
function isActiveRoute(routePath: string) {
	return route.path === routePath;
}

defineExpose({
	isActiveRoute,
});
</script>

<style lang="scss" scoped>
$expanded-width: var(--ls-sidebar-expanded-width, 200px);
$collapsed-width: var(--ls-sidebar-collapsed-width, calc(44px + 2 * 0.8rem));

section {
	display: flex;
}
section.app-sidebar {
	position: fixed;
	flex-direction: column;
	width: $collapsed-width;
	min-width: $collapsed-width;
	top: 0px;
	left: 0;
	bottom: 0;
	background-color: var(--background-75);
	border-right: 1px solid var(--border-color-primary);
	transition:
		width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		min-width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

	// .pinned-shut-unlock {
	// 	position: absolute;
	// 	top: 50%;
	// 	left: 8px;
	// 	transform: translateY(-50%);
	// 	width: 2.5rem;
	// 	height: 2.5rem;
	// 	border-radius: 50%;
	// 	background-color: var(--background-75);
	// 	border: 1px solid var(--background-400);
	// 	cursor: pointer;
	// 	display: flex;
	// 	align-items: center;
	// 	justify-content: center;
	// 	opacity: 0;
	// 	pointer-events: none;
	// 	transition:
	// 		opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
	// 		background-color 0.2s ease,
	// 		transform 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
	// 	z-index: 10;
	// 	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

	// 	i {
	// 		font-size: 1rem;
	// 		color: var(--text-50);
	// 		transition: color 0.2s ease;
	// 	}

	// 	&:hover {
	// 		background-color: var(--background-300);

	// 		i {
	// 			color: var(--ls-text-color);
	// 		}
	// 	}

	// 	// @media (prefers-color-scheme: light) {
	// 	// 	border-color: var(--ls-border-7-color);
	// 	// }
	// }

	.app-sidebar--content {
		position: relative;
		height: 100%;
		transition:
			opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
			width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
		width: 100%;
		box-sizing: border-box;
		overflow: hidden;
	}

	&.app-sidebar--pinned-shut {
		width: $collapsed-width;
		min-width: $collapsed-width;

		// .pinned-shut-unlock {
		// 	opacity: 1;
		// 	pointer-events: auto;
		// }
	}

	// Hide edit button in default mode, show on hover (always show when editing)
	:deep() .edit-button:not(.editing) {
		opacity: 0 !important;
		pointer-events: none !important;
	}

	&:hover :deep() .edit-button.visible:not(.editing) {
		opacity: 1 !important;
		pointer-events: auto !important;
	}

	&.app-sidebar--open:not(.app-sidebar--pinned-shut) {
		width: $expanded-width;
		min-width: $expanded-width;

		:deep() .sub-menu-item {
			a {
				padding: 0.5rem 0.8rem;

				h3 {
					max-width: 120px;
					opacity: 1;
					margin-left: 0.8rem;
				}
			}
		}

		.logout-button {
			p {
				max-width: 80px;
				opacity: 1;
				margin-left: 0.5rem;
			}
		}
	}

	.sidebar-items {
		.sidebar-item {
			.toggleable-header {
				:deep() img,
				:deep() svg {
					height: v-bind(logoHeightStyle);
				}
			}
		}
	}
}

@media (prefers-color-scheme: dark) {
	section.app-sidebar {
		background-color: var(--background-75);
	}
}

.sidebar-items {
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	overflow: hidden;

	.first-group {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		padding: 1rem 0.8rem;
	}

	.sidebar-item {
		font-size: 0.9rem;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		margin-bottom: 1.5rem;
		width: 100%;

		h4 {
			line-height: 30px;
			color: var(--text-500);
		}

		.toggleable-header {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: 30px;
			position: relative;
			transition: height 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

			:deep() img,
			:deep() svg {
				width: auto;
				max-width: 90%;
				transition:
					width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
					height 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
				position: relative;
				top: 0.25rem;
			}
		}
	}
}

.sidebar-item-header {
	width: 100%;
	height: 30px;
}

.navigation-menu {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: 100%;

	:deep() .sub-menu-item {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;

		&.active {
			a {
				width: 100%;
				color: var(--text-50);
				border-color: var(--background-500);
				background-color: var(--background-200);

				// @media (prefers-color-scheme: light) {
				// 	background-color: var(--ls-background-4-color);
				// }
			}
		}

		a {
			display: flex;
			flex-direction: row;
			align-items: center;
			position: relative;
			border: 1px solid transparent;
			border-radius: 0.25rem;
			color: var(--text-400);
			fill: var(--text-400);
			width: 100%;
			padding: 0.5rem 0.5rem;
			transition:
				padding-left 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				padding-right 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				border-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				background-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

			&:hover {
				background-color: var(--background-200);
				color: var(--text-50);
				border-color: var(--background-500);
			}

			svg,
			img {
				width: 26px;
				min-width: 26px;
				height: 26px;
				min-height: 26px;
				position: relative;
				top: 1px;
				color: inherit;
				fill: inherit;
			}

			h3 {
				font-size: 1.5rem;
				font-weight: 500;
				max-width: 0;
				margin-left: 0rem;
				opacity: 0;
				color: inherit;
				transition:
					max-width 0.25s cubic-bezier(0.075, 0.82, 0.165, 1),
					margin-left 0.25s cubic-bezier(0.075, 0.82, 0.165, 1),
					opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
			}
		}
	}
}

.last-group {
	.button-wrapper {
		width: 100%;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: 0.8rem;
		padding: 0.5rem 0.8rem;
		font-size: 1.15rem;

		svg,
		img {
			width: 20px;
			height: 20px;
		}

		:deep(.p-button) {
			width: 100%;

			svg {
				color: var(--ls-text-color);
			}
		}
	}

	.logout-button {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		background-color: var(--ls-primary-legible-color);
		color: var(--ls-text-invert-color);
		border-radius: 8px;
		cursor: pointer;
		margin-bottom: 1rem;
		gap: 0rem;

		p {
			color: inherit;
			transition:
				max-width 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				margin-left 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
			opacity: 0;
			max-width: 0px;
			margin-left: 0rem;
		}

		svg,
		img {
			color: inherit;
			width: 20px;
			height: 20px;
			position: relative;
			top: 1px;
		}

		&:hover {
			background-color: var(--ls-primary-color);
		}
	}

	.account-selector-container {
		background-color: var(--background-75);
		padding: 0.5rem 0.8rem;
		border-top: 1px solid var(--border-color-primary);
		height: calc(4rem + 2px);
		box-sizing: border-box;

		display: flex;
		align-items: center;
	}

	.logout-button-container {
		padding: 0.5rem;
	}

	.lock-sidebar-group {
		width: 100%;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		gap: 0.8rem;
		padding: 0.5rem 0.8rem;
		opacity: 0.6;
		transition: opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
		box-sizing: border-box;

		.lock-toggle {
			border-color: var(--background-300);
			min-width: 32px;
			min-height: 32px;
			transform: scale(0.9);
			transition:
				transform 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
				left 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

			:deep() {
				background-color: var(--background-200);
				color: var(--text-100);
			}
		}

		&.locked {
			opacity: 0.2;
		}

		&:hover {
			opacity: 1;

			.lock-toggle {
				transform: scale(0.85);
			}
		}

		&.hide {
			opacity: 0;
		}
	}

	.lock-button-wrapper {
		position: relative;

		.lock-toggle {
			min-width: 44px;
			min-height: 44px;
		}

		.icon {
			position: absolute;
			top: 50%;
			left: 50%;
			width: 16px;
			height: 16px;
			transform: translate(-50%, -50%);
			pointer-events: none;
			opacity: 0.7;
			color: var(--text-300);
		}
	}
}

:root[data-theme="dark"] {
	.last-group .account-selector-container :deep() {
		// border-top-color: var(--background-200);
	}
	.navigation-menu {
		:deep() .sub-menu-item {
			&.active {
				a {
					background-color: var(--background-200);
				}

				&:hover {
					a {
						border-color: var(--background-400);
						background-color: var(--background-200);
					}
				}
			}
			&:hover {
				a {
					border-color: var(--background-400);
					background-color: var(--background-100);
				}
			}
		}
	}
}
:root[data-theme="light"] {
	.last-group .account-selector-container :deep() {
		// border-top-color: var(--background-300);
	}
	.navigation-menu {
		:deep() .sub-menu-item {
			&.active {
				a {
					background-color: var(--background-300);
				}

				&:hover {
					a {
						border-color: var(--background-500);
						background-color: var(--background-300);
					}
				}
			}
			&:hover {
				a {
					border-color: var(--background-500);
					background-color: var(--background-200);
				}
			}
		}
	}
}
</style>
